import { useState } from "react";
import LobbyLayout from "@/components/LobbyLayout";
import UnavailableDialog from "@/components/UnavailableDialog";
import { SCREEN } from "@/stores/gameStore";
import { useT } from "@/i18n";
import { Lock, ShoppingBag } from "lucide-react";

// Placeholder content — real store activates in the next content season.
const CATEGORIES = [
    { id: "featured", label: "FEATURED" },
    { id: "weapons", label: "WEAPON SKINS" },
    { id: "characters", label: "AGENTS" },
    { id: "bundles", label: "BUNDLES" },
    { id: "capsules", label: "CAPSULES" },
];

const PLACEHOLDER_TILES = Array.from({ length: 8 }).map((_, i) => ({
    id: i,
    name: ["Wolfline", "Ash Cinder", "Chrome Void", "Sand Recon", "Amber Ops", "Cobalt Wire", "Fracture", "Midnight Steel"][i % 8],
    tier: ["COMMON", "RARE", "EPIC", "LEGENDARY"][i % 4],
    accent: ["#7a8b95", "#c084fc", "#ffb300", "#ff7043"][i % 4],
}));

export default function StoreScreen() {
    const t = useT();
    const [dialog, setDialog] = useState(true); // open on entry per spec
    const [cat, setCat] = useState("featured");

    return (
        <LobbyLayout active={SCREEN.STORE}>
            <div data-testid="store-screen" className="w-full h-full overflow-hidden px-8 py-6 flex flex-col">
                <div className="mb-4">
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                        {t("store.subtitle")}
                    </div>
                    <div className="axis-heading text-3xl tracking-[0.4em] text-white flex items-center gap-3">
                        <ShoppingBag size={22} className="text-orange-400" />
                        {t("store.title")}
                    </div>
                </div>

                {/* Category tabs */}
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3 overflow-x-auto">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setCat(c.id)}
                            className={`axis-cut-sm px-4 py-2 axis-mono text-[11px] tracking-[0.35em] transition border ${
                                cat === c.id
                                    ? "border-white text-white bg-white/5"
                                    : "border-transparent text-white/50 hover:text-white"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                {/* Locked overlay + tile grid */}
                <div className="relative flex-1 overflow-hidden">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 h-full overflow-y-auto pr-2 pb-6 opacity-40">
                        {PLACEHOLDER_TILES.map((it) => (
                            <div
                                key={it.id}
                                className="axis-cut-sm border border-white/10 p-4 bg-black/40"
                            >
                                <div
                                    className="h-28 axis-cut-sm mb-3"
                                    style={{
                                        background: `linear-gradient(135deg, ${it.accent}55 0%, #0a0908 100%)`,
                                    }}
                                />
                                <div className="axis-heading text-sm text-white/90 tracking-wider">
                                    {it.name}
                                </div>
                                <div
                                    className="axis-mono text-[9px] tracking-[0.3em] mt-1"
                                    style={{ color: it.accent }}
                                >
                                    {it.tier}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Lock overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-transparent to-black/70 pointer-events-none">
                        <div className="axis-glass axis-cut px-8 py-6 flex flex-col items-center pointer-events-auto">
                            <Lock size={26} className="text-orange-300 mb-3" />
                            <div className="axis-heading text-xl tracking-[0.4em] text-white">
                                {t("store.locked_title").toUpperCase()}
                            </div>
                            <div className="mt-2 axis-mono text-[11px] text-white/60 tracking-[0.15em] max-w-md text-center">
                                {t("store.locked_message")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <UnavailableDialog
                open={dialog}
                onClose={() => setDialog(false)}
                title={t("store.locked_title")}
                message={t("store.locked_message")}
            />
        </LobbyLayout>
    );
}
